const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('sbwom_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('sbwom_token', token);
    } else {
      localStorage.removeItem('sbwom_token');
    }
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Always get fresh token from localStorage
    const token = localStorage.getItem('sbwom_token') || sessionStorage.getItem('sbwom_token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getHeaders(),
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Authentication methods
  async login(email, password) {
    const response = await this.post('/auth/login', { email, password });
    this.setToken(response.token);
    return response;
  }

  async register(userData) {
    const response = await this.post('/auth/register', userData);
    this.setToken(response.token);
    return response;
  }

  async googleLogin(idToken) {
    const response = await this.post('/auth/google', { idToken });
    this.setToken(response.token);
    return response;
  }

  async forgotPassword(email) {
    return this.post('/auth/forgot-password', { email });
  }

  async resetPassword(token, password) {
    return this.post('/auth/reset-password', { token, password });
  }

  async getProfile() {
    return this.get('/auth/profile');
  }

  // Campaign methods
  async getCampaigns(limit = 50) {
    return this.get(`/campaigns?limit=${limit}`);
  }

  async getPublicCampaigns(limit = 50) {
    return this.get(`/campaigns/public?limit=${limit}`);
  }

  async getUserCampaigns() {
    return this.get('/campaigns/user');
  }

  async getCampaign(id) {
    return this.get(`/campaigns/${id}`);
  }

  async createCampaign(campaignData) {
    return this.post('/campaigns', campaignData);
  }

  async updateCampaign(id, campaignData) {
    return this.put(`/campaigns/${id}`, campaignData);
  }

  async cancelCampaign(id) {
    return this.post(`/campaigns/${id}/cancel`);
  }

  async deleteCampaign(id) {
    return this.delete(`/campaigns/${id}`);
  }

  async recordCampaignView(id) {
    return this.post(`/campaigns/${id}/view`);
  }

  async getCampaignStats(id) {
    return this.get(`/campaigns/${id}/stats`);
  }

  // Network methods
  async getNetworkCluster() {
    return this.get('/network/cluster');
  }

  async getNetworkTree(userId) {
    return this.get(`/network/tree/${userId}`);
  }

  async createInvite() {
    return this.post('/network/invite');
  }

  async getNetworkStats() {
    return this.get('/network/stats');
  }

  async mergeNetworkTrees(targetUserId) {
    return this.post('/network/merge', { targetUserId });
  }

  async mergeNetworkByCode(inviteCode) {
    return this.post('/network/merge-by-code', { inviteCode });
  }

  // Contact methods
  async suggestContacts(emails) {
    return this.post('/contacts/suggest', { emails });
  }

  async importGoogleContacts(contacts) {
    return this.post('/contacts/import/google', { contacts });
  }

  async importFacebookContacts(contacts) {
    return this.post('/contacts/import/facebook', { contacts });
  }

  async claimContactKey(key) {
    return this.post('/contacts/claim-key', { key });
  }

  // Identity methods
  async bindIdentities(identities) {
    return this.post('/identities/bind', { identities });
  }

  async getContactKey() {
    return this.get('/identities/contact-key');
  }

  async getIdentities() {
    return this.get('/identities');
  }

  async deleteIdentity(type, value) {
    return this.delete(`/identities/${type}/${value}`);
  }

  // Activity methods
  async getActivityScore() {
    return this.get('/activity/score');
  }

  async getActivityHistory(limit = 50, offset = 0) {
    return this.get(`/activity/history?limit=${limit}&offset=${offset}`);
  }

  async getLeaderboard(limit = 20) {
    return this.get(`/activity/leaderboard?limit=${limit}`);
  }

  async trackActivity(activityData) {
    return this.post('/activity/track', activityData);
  }

  async getActivityStats() {
    return this.get('/activity/stats');
  }

  // Rating methods
  async rateCampaign(campaignId, rating) {
    return this.post(`/ratings/${campaignId}`, { rating });
  }

  async getCampaignRatings(campaignId) {
    return this.get(`/ratings/${campaignId}`);
  }

  async getUserRatings(userId) {
    return this.get(`/ratings/user/${userId}`);
  }

  async deleteCampaignRating(campaignId) {
    return this.delete(`/ratings/${campaignId}`);
  }
}

const apiService = new ApiService();

export default apiService;
